import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Stack,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  IconButton,
} from '@mui/material';
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Close as CloseIcon,
  FormatListBulleted as FormatListBulletedIcon,
  AttachMoney as AttachMoneyIcon,
  Payment as PaymentIcon,
  DateRange as DateRangeIcon,
} from '@mui/icons-material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

import {
  paymentOptionsProcessingFeesActions,
  selectAvailableProcessingFeeLoB,
  selectAvailableProcessingFeeCurrency,
  selectAvailableProcessingFeeMethodCode,
  selectProcessingFeeFilteredLoB,
  selectProcessingFeeFilteredCurrency,
  selectProcessingFeeFilteredMethodCode,
  selectProcessingFeeFilteredStartDate,
  selectProcessingFeeFilteredEndDate,
} from '../../store/payment-options-processing-fees';

function FilterListItemHeader({
  title,
  onClick,
  isExpanded,
  isDisabled = false,
  icon,
}) {
  return (
    <ListItemButton
      onClick={onClick}
      disabled={isDisabled}>
      <ListItemIcon>{icon}</ListItemIcon>
      <ListItemText primary={title} />
      {isExpanded && !isDisabled ? <ExpandLessIcon /> : <ExpandMoreIcon />}
    </ListItemButton>
  );
}

function FilterListCollapsableListItem({
  isExpanded,
  items = [],
  selection = [],
  onSelection,
}) {
  return (
    <Collapse
      in={isExpanded}
      unmountOnExit>
      <List
        component="div"
        sx={{
          maxHeight: '180px',
          overflow: 'scroll',
        }}
        disablePadding>
        {items.map((item) => (
          <ListItemButton
            key={item}
            dense
            sx={{ pl: 4 }}
            onClick={() => onSelection(item)}>
            <ListItemIcon>
              <Checkbox
                checked={selection.includes(item)}
                tabIndex={-1}
                disableRipple
              />
            </ListItemIcon>
            <ListItemText primary={item} />
          </ListItemButton>
        ))}
      </List>
    </Collapse>
  );
}

function FilterListCollapsableView({ isExpanded, children }) {
  return <Collapse in={isExpanded}>{children}</Collapse>;
}

export default function ProcessingFeesFilter({ isVisible = false, onClose }) {
  const dispatch = useDispatch();

  const availableProcessingFeeLoB = useSelector(
    selectAvailableProcessingFeeLoB,
  );

  const availableProcessingFeeCurrency = useSelector(
    selectAvailableProcessingFeeCurrency,
  );
  const availableProcessingFeeMethodCode = useSelector(
    selectAvailableProcessingFeeMethodCode,
  );

  // Filter - LoB
  const filteredLoB = useSelector(selectProcessingFeeFilteredLoB);
  const [selectedLoB, setSelectedLoB] = useState(filteredLoB);
  const [isLoBFilterExpanded, setIsLoBFilterExpanded] = useState(true);

  // Filter - Currency
  const filteredCurrency = useSelector(selectProcessingFeeFilteredCurrency);
  const [selectedCurrency, setSelectedCurrency] = useState(filteredCurrency);
  const [isCurrencyFilterExpanded, setIsCurrencyFilterExpanded] =
    useState(true);

  // Filter - Method Code
  const filteredMethodCode = useSelector(selectProcessingFeeFilteredMethodCode);
  const [selectedMethodCode, setSelectedMethodCode] =
    useState(filteredMethodCode);
  const [isMethodCodeFilterExpanded, setIsMethodCodeFilterExpanded] =
    useState(true);

  // Filter - Start Date
  const filteredStartDate = useSelector(selectProcessingFeeFilteredStartDate);
  const [selectedStartDate, setSelectedStartDate] = useState(filteredStartDate);

  // Filter - End Date
  const filteredEndDate = useSelector(selectProcessingFeeFilteredEndDate);
  const [selectedEndDate, setSelectedEndDate] = useState(filteredEndDate);

  const [isDateRangeFilterExpanded, setIsDateRangeFilterExpanded] =
    useState(true);

  useEffect(() => {
    setSelectedLoB(filteredLoB);
  }, [filteredLoB]);

  useEffect(() => {
    setSelectedCurrency(filteredCurrency);
  }, [filteredCurrency]);

  useEffect(() => {
    setSelectedMethodCode(filteredMethodCode);
  }, [filteredMethodCode]);

  useEffect(() => {
    setSelectedStartDate(filteredStartDate);
  }, [filteredStartDate]);

  useEffect(() => {
    setSelectedEndDate(filteredEndDate);
  }, [filteredEndDate]);

  const onFilterSelection = (selection, oldSelections, setSelections) => {
    const isAdded = oldSelections.includes(selection);
    let newSelections = [];

    if (isAdded) {
      newSelections = oldSelections.filter(
        (oldSelection) => oldSelection !== selection,
      );
    } else {
      newSelections = [...oldSelections, selection];
    }

    setSelections(newSelections);
  };

  const handleClose = () => {
    onClose(false);

    // Set default values
    setSelectedLoB(filteredLoB);
    setSelectedCurrency(filteredCurrency);
    setSelectedMethodCode(filteredMethodCode);
    setSelectedStartDate(filteredStartDate);
    setSelectedEndDate(filteredEndDate);
  };

  const onApplyClickHandler = () => {
    const {
      setFilterLoB,
      setFilterCurrency,
      setFilterMethodCode,
      setFilterStartDate,
      setFilterEndDate,
    } = paymentOptionsProcessingFeesActions;

    dispatch(setFilterLoB(selectedLoB));
    dispatch(setFilterCurrency(selectedCurrency));
    dispatch(setFilterMethodCode(selectedMethodCode));
    dispatch(setFilterStartDate(selectedStartDate));
    dispatch(setFilterEndDate(selectedEndDate));

    handleClose();
  };

  const onResetClickHandler = () => {
    const { resetFilters } = paymentOptionsProcessingFeesActions;

    dispatch(resetFilters());

    handleClose();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="sm"
      open={isVisible}
      onClose={handleClose}>
      <DialogTitle>Processing Fees Filter</DialogTitle>
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
        }}>
        <CloseIcon />
      </IconButton>
      <DialogContent
        sx={{
          p: 0,
        }}>
        <List component="nav">
          <FilterListItemHeader
            title="Payment Mode"
            icon={<FormatListBulletedIcon />}
            onClick={() => setIsLoBFilterExpanded((prev) => !prev)}
            isExpanded={isLoBFilterExpanded}
          />
          <FilterListCollapsableListItem
            isExpanded={isLoBFilterExpanded}
            items={availableProcessingFeeLoB}
            selection={selectedLoB}
            onSelection={(selection) => {
              onFilterSelection(selection, selectedLoB, setSelectedLoB);
            }}
          />

          <Divider />

          <FilterListItemHeader
            title="Currency"
            icon={<AttachMoneyIcon />}
            onClick={() => setIsCurrencyFilterExpanded((prev) => !prev)}
            isExpanded={isCurrencyFilterExpanded}
          />
          <FilterListCollapsableListItem
            isExpanded={isCurrencyFilterExpanded}
            items={availableProcessingFeeCurrency}
            selection={selectedCurrency}
            onSelection={(selection) => {
              onFilterSelection(
                selection,
                selectedCurrency,
                setSelectedCurrency,
              );
            }}
          />

          <Divider />

          <FilterListItemHeader
            title="Method code"
            icon={<PaymentIcon />}
            onClick={() => setIsMethodCodeFilterExpanded((prev) => !prev)}
            isExpanded={isMethodCodeFilterExpanded}
          />
          <FilterListCollapsableListItem
            isExpanded={isMethodCodeFilterExpanded}
            items={availableProcessingFeeMethodCode}
            selection={selectedMethodCode}
            onSelection={(selection) => {
              onFilterSelection(
                selection,
                selectedMethodCode,
                setSelectedMethodCode,
              );
            }}
          />

          <Divider />

          <FilterListItemHeader
            title="Date range"
            icon={<DateRangeIcon />}
            onClick={() => setIsDateRangeFilterExpanded((prev) => !prev)}
            isExpanded={isDateRangeFilterExpanded}
          />
          <FilterListCollapsableView isExpanded={isDateRangeFilterExpanded}>
            <Stack
              sx={{
                flexDirection: 'row',
                justifyContent: 'space-between',
                gap: '24px',
                padding: '12px 42px',
              }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  format="DD/MM/YYYY"
                  label="Start Date"
                  value={selectedStartDate ? dayjs(selectedStartDate) : null}
                  onChange={(newDate) => {
                    setSelectedStartDate(newDate);
                    setSelectedEndDate(dayjs(newDate).add(1, 'day'));
                  }}
                  slotProps={{
                    field: { fullWidth: true },
                  }}
                />
              </LocalizationProvider>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  format="DD/MM/YYYY"
                  label="End Date"
                  value={selectedEndDate ? dayjs(selectedEndDate) : null}
                  onChange={setSelectedEndDate}
                  slotProps={{
                    field: { fullWidth: true },
                  }}
                  minDate={dayjs(selectedStartDate)}
                />
              </LocalizationProvider>
            </Stack>
          </FilterListCollapsableView>
        </List>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onResetClickHandler}
          variant="outlined">
          Reset
        </Button>
        <Button
          onClick={onApplyClickHandler}
          variant="contained">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
